import React, { useState, useEffect, useRef } from "react";
import {
	Box, Grid, Container, Card, CardContent, Divider, Typography, TextField, Select, MenuItem, InputLabel, FormControl, Checkbox,
	Chip, Button, IconButton, LinearProgress, CircularProgress, Dialog, DialogTitle, DialogContent, ListItemText, Autocomplete,
	DialogActions, Accordion, AccordionSummary, AccordionDetails, Snackbar, Backdrop, InputAdornment, Alert, FormLabel, OutlinedInput,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InventoryIcon from "@mui/icons-material/Inventory";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SettingsIcon from "@mui/icons-material/Settings";
import LayersIcon from "@mui/icons-material/Layers";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { GlobalStyles } from "@mui/material";
import {
	storeValue,
	SelectSaveProductCompleted,
	resetSaveProductCompleted, saveProductThunk, SAVE_STEP_LABELS,
} from "../AddProducts/AddProductsSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { upsertVariant } from "./VariantService";
import { ThemeProvider } from "@mui/material/styles";
import { buttonTheme } from "../../theme";
import { config } from "../../config";
import { getUserName } from "../../utils/authSession";
import ImageGuidelinesDialog from "../ImageGuidelines/ImageGuidelinesDialog";
import { aiErrorMessage } from "../../utils/aiErrors";

const colorPalette = [
	"#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e", "#10b981",
	"#14b8a6", "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7",
	"#d946ef", "#ec4899", "#f43f5e", "#000000", "#374151", "#6b7280", "#9ca3af",
	"#d1d5db", "#e5e7eb", "#ffffff",
];

const sizeCategories = {
	Clothing: {
		Men: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
		Women: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
		Children: ["0-3 months", "3-6 months", "6-12 months", "1-2 years", "3-4 years",
			"5-6 years", "7-8 years", "9-10 years", "11-12 years"],
		Unisex: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
	},
	Shoes: {
		"US Men": ["7", "8", "9", "10", "11", "12", "13"],
		"US Women": ["5", "6", "7", "8", "9", "10", "11"],
		"UK Men": ["6", "7", "8", "9", "10", "11", "12"],
		"UK Women": ["3", "4", "5", "6", "7", "8", "9"],
		"EU Men": ["40", "41", "42", "43", "44", "45", "46"],
		"EU Women": ["36", "37", "38", "39", "40", "41", "42"],
		Children: ["8C", "9C", "10C", "11C", "12C", "13C", "1Y", "2Y", "3Y", "4Y", "5Y", "6Y"],
	},
	Accessories: {
		Hats: ["S", "M", "L", "XL", "One size"],
		Belts: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
		Gloves: ["XS", "S", "M", "L", "XL"],
		Scarves: ["One size"],
		Sunglasses: ["One size"],
	},
};

const deliveryOptionsList = ["Store Pickup", "Home Delivery"];

const DELIVERY_SYNONYMS = {
	"pickup": "Store Pickup",
	"store pickup": "Store Pickup",
	"store_pickup": "Store Pickup",
	"store-pickup": "Store Pickup",

	"home delivery": "Home Delivery",
	"homedelivery": "Home Delivery",
	"home_delivery": "Home Delivery",
	"home-delivery": "Home Delivery",
	"delivery": "Home Delivery",
};

// Turn API value (string or array) into clean UI labels array
const normalizeDeliveryFromAPI = (val) => {
	const arr = Array.isArray(val) ? val : (val || "").split(",");
	return arr
		.map((s) => {
			const k = String(s).trim().toLowerCase();
			return DELIVERY_SYNONYMS[k] || ""; // map unknowns to "" (will be filtered)
		})
		.filter(Boolean)                                   // drop unknowns
		.filter((v, i, a) => a.indexOf(v) === i)           // dedupe
		.filter((v) => deliveryOptionsList.includes(v));   // keep only valid labels
};

// Turn UI label back into API code(s) for saving
const toApiDeliveryCode = (label) => {
	const k = String(label).trim().toLowerCase();
	if (k === "store pickup") return "pickup";           // known API code
	if (k === "home delivery") return "home delivery";   // keep as-is (no code given)
	return label; // fallback (keeps things working if new options appear)
};

const additionalInfoOptions = [
	"Contains Added Preservatives",
	"Contains Chemicals",
	"Contains Lactose Substitutes",
	"contains Heavy Parts",
	"Fragile",
	"Keep Refrigerated",
	"Flammable",
	"Choking Hazard",
	"Gluten Free",
	"Vegan",
	"Organic",
	"Non-GMO",
	"Contains Nuts",
	"Sugar Free",
	"Batteries Included",
	"Batteries Not Included",
	"Assembly Required",
	"Waterproof",
	"Recyclable",
	"Biodegradable",
	"Eco-Friendly"
];

const sizeOptions = [];
Object.entries(sizeCategories).forEach(([category, subCats]) => {
	Object.entries(subCats).forEach(([sub, sizes]) => {
		sizes.forEach((sz) => {
			sizeOptions.push({
				category,
				label: `${sub}: ${sz}`,
				value: sz,
			});
		});
	});
});

const filterOptions = createFilterOptions();

const HEX_TO_NAME = {
	"#ef4444": "Red", "#f97316": "Orange", "#f59e0b": "Amber",
	"#eab308": "Yellow", "#84cc16": "Lime", "#22c55e": "Green",
	"#10b981": "Emerald", "#14b8a6": "Teal", "#06b6d4": "Cyan",
	"#0ea5e9": "Sky", "#3b82f6": "Blue", "#6366f1": "Indigo",
	"#8b5cf6": "Violet", "#a855f7": "Purple", "#d946ef": "Fuchsia",
	"#ec4899": "Pink", "#f43f5e": "Rose", "#000000": "Black",
	"#374151": "Slate", "#6b7280": "Gray", "#9ca3af": "Zinc",
	"#d1d5db": "Stone", "#e5e7eb": "Neutral", "#ffffff": "White",
};
const nameFromHex = (hex) => HEX_TO_NAME[hex?.toLowerCase()] || "Custom";

const rgbToHex = (rgbStr) => {
	// accept "rgb(r,g,b)" or "rgba(r,g,b,a)"; return "" if not a string / malformed
	if (typeof rgbStr !== "string" || !rgbStr.trim()) return "";
	const parts = rgbStr.match(/\d+/g);
	if (!parts || parts.length < 3) return "";
	const [r, g, b] = parts;
	return (
		"#" +
		[r, g, b]
			.map((n) => Number(n).toString(16).padStart(2, "0"))
			.join("")
	);
};

const prefsToMap = arr =>
	arr.reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});

export default function EditProduct() {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

	// --- manage-products selection bridge (minimal, no refactor) ---
	const MP_SEL_KEY = "mp.selection";

	const loadMpSelection = () => {
		try {
			return JSON.parse(sessionStorage.getItem(MP_SEL_KEY) || "{}");
		} catch {
			return {};
		}
	};

	const saveSelection = (sel) => {
		// keep the exact key/shape ManageProducts expects
		sessionStorage.setItem(MP_SEL_KEY, JSON.stringify(sel || {}));
	};

	// pull the *already-saved* selection so the vars exist for the back button
	const {
		departmentId: selectedDepartmentId = "",
		categoryId: selectedCategoryId = "",
		subCategoryId: selectedSubCategoryId = "",
	} = loadMpSelection();


	const numberFieldProps = {
		// native number field
		inputProps: { min: 0 },
		// block mouse wheel from changing the value
		onWheel: (e) => e.currentTarget.blur(),
		// block invalid keys
		onKeyDown: (e) => {
			if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
		},
	};

	// clamp helper: '' → '' (let user clear), else non-negative number
	const clampToNonNegative = (raw, isFloat = false) => {
		if (raw === "" || raw === null || raw === undefined) return "";
		const n = isFloat ? parseFloat(raw) : parseInt(raw, 10);
		if (Number.isNaN(n)) return "";
		return Math.max(0, n);
	};

	// update helpers (main form vs variant form)
	const onNumChange = (key, isFloat = false) => (e) => {
		const v = clampToNonNegative(e.target.value, isFloat);
		setFormData((p) => ({ ...p, [key]: v }));
	};
	const onNumChangeV = (key, isFloat = false) => (e) => {
		const v = clampToNonNegative(e.target.value, isFloat);
		setVariantForm((p) => ({ ...p, [key]: v }));
	};

	const [loadBackdrop, setLoadBackdrop] = useState(false);

	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const {
		product = {},
		images = {},
		inventory = {},
		pricing = {},
		fulfilment = {},
		additionInfo = {},
		productPreference = [],
	} = location.state?.data || {};

	const pref = prefsToMap(productPreference);
	const [selectedColor, setSelectedColor] = useState(
		pref?.colorCode ? rgbToHex(pref.colorCode) : "#000000"
	);

	const initialVariants = Array.isArray(location.state?.data1)
		? location.state.data1
		: [];

	/* ---------- helper to flatten image sections into array ----- */
	const extractImages = (imgObj = {}) => {
		const sections = [
			"primary",
			"imgSec1",
			"imgSec2",
			"imgSec3",
			"imgSec4",
		];
		const arr = [];
		sections.forEach((sec) => {
			if (Array.isArray(imgObj[sec]) && imgObj[sec].length > 0) {
				arr.push(imgObj[sec][0].presignedURL);
			}
		});
		return arr;
	};

	const [formData, setFormData] = useState({
		productName: product.name || "",
		displayName: product.displayName || "",
		shortDescription: product.shortDescription || "",
		description: product.description || "",

		barcode: pricing.barcode || inventory.barcode || "",
		sku: pricing.sku || inventory.sku || "",
		pricePerUnit: pricing.pricePerUnit || 0,
		units: pricing.units || 0,
		quantity: pricing.quantity || 0,
		totalPrice: pricing.totalPrice || 0,
		contactSupplier: pricing.contactSupplierFlag || false,

		maxStock: inventory.maxStock || 0,
		currentStock: inventory.inStock || 0,
		lowStock: inventory.lowStock || 0,
		lowStockTracking: inventory.lowStockTrack || false,

		productColorName: pref.color || "",
		productColorValue: pref?.colorCode ? rgbToHex(pref.colorCode) : "",
		productSize: pref.size || "",

		fulfillmentMethod: fulfilment.fulfillment || "",
		productStatus: fulfilment.productStatus || "",
		deliveryOptions: normalizeDeliveryFromAPI(fulfilment.deliveryOption),
		shippingWeight: additionInfo.shippingWeight || 0,
		images: extractImages(images),
		imageFileObjects: [],
		variants: initialVariants,

		supplierInfo: {
			companyName: "", contactName: "", contactEmail: "", contactPhone: "",
			website: "", address1: "", address2: "", city: "", state: "", postal: "", country: "",
		},
		additionalInfo:
			additionInfo.specialIndicators
				? additionInfo.specialIndicators.split(",").map(s => s.trim()).filter(s => s !== "indicate if product contains alergic contents")
				: [],
	});

	const [activeSection, setActiveSection] = useState("product-details");
	const [variantDialogOpen, setVariantDialogOpen] = useState(false);
	const [guidelineOpen, setGuidelineOpen] = useState(false);
	const [editingVariant, setEditingVariant] = useState(null);

	const [variantForm, setVariantForm] = useState({
		color: "", colorCode: "", size: "",
		lowStock: 0, maxStock: 0, currentStock: 0,
		pricePerUnit: 0, units: 0, quantity: 0, totalPrice: 0,
		barcode: "", sku: "", images: [],
	});

	const vendorId = product.vendorId;
	const productId = product.id;
	const sessionToken = () =>
		sessionStorage.getItem("sessionToken") || localStorage.getItem("sessionToken");

	const [variantIdValue, setVariantIdValue] = useState(null);
	const [variantPricingId, setVariantPricingId] = useState(null);
	const [variantInventoryId, setVariantInventoryId] = useState(null);
	const [variantPreferenceColorId, setVariantPreferenceColorId] = useState(null);
	const [variantPreferenceColorCodeId, setVariantPreferenceColorCodeId] = useState(null);
	const [variantPreferenceSizeId, setVariantPreferenceSizeId] = useState(null);

	const dispatch = useDispatch();
	const saveProductCompleted = useSelector(SelectSaveProductCompleted);

	const API_BASE = `${config.API_MSWSSTOREMANAGER_BASE_URL}/v2`;
	const json = data => ({
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"X-Session-Token": sessionToken()
		},
		body: JSON.stringify(data),
	});
	const post = (path, body) => fetch(`${API_BASE}/${path}`, json(body)).then(r => r.json());
	const uploadImage = ({ file, imageType, productId, vendorId, variantId }) => {
		const fd = new FormData();
		fd.append("bucketname", "microstores.net");
		fd.append("uploadfile", file);
		fd.append("productid", productId);
		fd.append("vendorid", vendorId);
		fd.append("imageType", imageType);
		fd.append("variantId", variantId);
		return fetch(`${API_BASE}/variant-images/uploadProductMediaFile`, { method: "POST", body: fd });
	};

	useEffect(() => {
		if (saveProductCompleted) {
			setSnackbar({ open: true, severity: "success", message: "Product saved." });
			setLoadBackdrop(false);
			dispatch(resetSaveProductCompleted());
		}
	}, [saveProductCompleted, dispatch]);

	const handleImageUpload = (e, isVariant = false) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;
		const all = Array.from(files);
		const ok = all.filter((f) => f.size / 1024 / 1024 <= 2);
		if (ok.length !== all.length) {
			alert("File size exceeds 2 MiB");
		}
		if (ok.length === 0) return;
		const urls = ok.map((f) => URL.createObjectURL(f));
		if (isVariant) {
			const newURLs = urls.slice(0, 5);
			const newFiles = ok.slice(0, 5);
			setVariantForm((prev) => ({
				...prev,
				images: [...prev.images, ...newURLs].slice(0, 5),
				imageFiles: [...(prev.imageFiles || []), ...newFiles].slice(0, 5),
			}));
		} else {
			setFormData((p) => ({
				...p,
				images: [...p.images, ...urls].slice(0, 5),
				imageFileObjects: [...p.imageFileObjects, ...ok].slice(0, 5),
			}));
		}
	};

	const removeImage = (idx, isVariant = false) => {
		if (isVariant) {
			setVariantForm((p) => ({
				...p,
				images: p.images.filter((_, i) => i !== idx),
				imageFiles: (p.imageFiles || []).filter((_, i) => i !== idx),
			}));
		} else {
			setFormData((p) => ({
				...p,
				images: p.images.filter((_, i) => i !== idx),
				imageFileObjects: p.imageFileObjects.filter((_, i) => i !== idx),
			}));
		}
	};

	const uploadVariantImage = (file, imageType, productId, vendorId, variantId) => {
		if (!file) return Promise.resolve();           // skip empty slots
		const fd = new FormData();
		fd.append("bucketname", "microstores.net");
		fd.append("uploadfile", file);
		fd.append("productid", productId);
		fd.append("vendorid", vendorId);
		fd.append("imageType", imageType);
		fd.append("variantId", variantId);
		return fetch(
			`${config.API_MSWSSTOREMANAGER_BASE_URL}/v2/variant-images/uploadProductMediaFile`,
			{ method: "POST", body: fd }
		);
	};

	const validateSection = (id) => {
		switch (id) {
			case "product-details":
				return formData.productName.trim() && formData.displayName.trim();
			case "pricing":
				return (
					formData.barcode.trim() &&
					formData.pricePerUnit > 0 &&
					formData.units > 0 &&
					formData.quantity > 0
				);
			case "images":
				return formData.images.length > 0;
			case "inventory":
				return formData.maxStock > 0 && formData.currentStock >= 0;
			case "variations":
				return true;
			case "shipping":
				return (
					formData.fulfillmentMethod &&
					formData.productStatus &&
					formData.deliveryOptions.length > 0
				);
			case "additional":
				return true;
			default:
				return false;
		}
	};

	const getCompletedSections = () =>
		[
			"product-details",
			"pricing",
			"images",
			"inventory",
			"variations",
			"shipping",
			"additional",
		].filter((sec) => validateSection(sec));

	const completionPct =
		(getCompletedSections().length / 7) * 100;

	useEffect(() => {
		if (!formData.contactSupplier && formData.pricePerUnit && formData.units && formData.quantity) {
			setFormData((prev) => ({
				...prev,
				totalPrice: prev.pricePerUnit * prev.units * prev.quantity,
			}));
		}
	}, [formData.pricePerUnit, formData.units, formData.quantity, formData.contactSupplier]);

	useEffect(() => {
		if (variantForm.pricePerUnit && variantForm.units && variantForm.quantity) {
			setVariantForm((prev) => ({
				...prev,
				totalPrice: prev.pricePerUnit * prev.units * prev.quantity,
			}));
		}
	}, [variantForm.pricePerUnit, variantForm.units, variantForm.quantity]);

	const resetVariantForm = () => {
		setVariantForm({
			color: "", colorCode: "", size: "",
			lowStock: 0, maxStock: 0, currentStock: 0,
			pricePerUnit: 0, units: 0, quantity: 0, totalPrice: 0,
			barcode: "", sku: "", images: [],
		});
		setEditingVariant(null);
		setVariantIdValue(null);
		setVariantPricingId(null);
		setVariantInventoryId(null);
		setVariantPreferenceColorId(null);
		setVariantPreferenceColorCodeId(null);
		setVariantPreferenceSizeId(null);
	};

	const handleVariantSubmit = async () => {
		/* ---------- 0. client‑side validation ---------- */
		const required = [
			variantForm.pricePerUnit,
			variantForm.units,
			variantForm.quantity,
			variantForm.barcode,
			variantForm.sku,
			variantForm.maxStock,
			variantForm.currentStock,
			variantForm.lowStock,
		];
		if (required.some((v) => !v) || variantForm.images.length === 0) {
			alert("Please fill all required fields and add at least one image.");
			return;
		}

		/* ---------- 1. derive code & payload parts ---------- */
		const colourName = variantForm.color || nameFromHex(variantForm.colorCode);
		const variantCode = [colourName, variantForm.size].filter(Boolean).join(" • ") || "Untitled";

		setLoadBackdrop(true);
		try {

			const saved = await upsertVariant({
				variant: { ...variantForm, variantCode },
				productId: product.id,
				vendorId: vendorId,
				ids: {
					pricingId: variantPricingId,
					inventoryId: variantInventoryId,
					preferenceColorId: variantPreferenceColorId,
					preferenceColorCodeId: variantPreferenceColorCodeId,
					preferenceSizeId: variantPreferenceSizeId,
				},
			});
			const savedId = saved.id;
			setVariantIdValue(savedId);
			setVariantPricingId(saved.pricingId);
			setVariantInventoryId(saved.inventoryId);
			setVariantPreferenceColorId(saved.preferenceColorId);
			setVariantPreferenceColorCodeId(saved.preferenceColorCodeId);
			setVariantPreferenceSizeId(saved.preferenceSizeId);

			// await Promise.all(
			// 	(variantForm.imageFiles || []).map((file, idx) =>
			// 		uploadImage({
			// 			file,
			// 			imageType: ['imgPrimary', 'imgSec1', 'imgSec2', 'imgSec3', 'imgSec4'][idx],
			// 			productId: product.id,
			// 			vendorId,
			// 			variantId: savedId,
			// 		})
			// 	)
			// );

			setFormData(prev => {
				const newVariant = { ...variantForm, id: savedId };
				return {
					...prev,
					variants: editingVariant
						? prev.variants.map(v => (v.id === savedId ? newVariant : v))
						: [...prev.variants, newVariant],
				};
			});
			setSnackbar({ open: true, severity: "success", message: "Variant saved!" });
			resetVariantForm();
			setVariantDialogOpen(false);
		} catch (err) {
			console.error(err);
			setSnackbar({ open: true, severity: "error", message: "Could not save variant." });
		} finally {
			setLoadBackdrop(false);
		}
	};

	const editVariant = async (variantObj) => {
		if (!variantObj?.id) return;
		setLoadBackdrop(true);
		try {
			const res = await fetch(
				`${API_BASE}/variant-details?vendorId=${encodeURIComponent(vendorId)}`,
				json({
					productId: product.id,
					variant_id: variantObj.id,
				})
			);
			const data = await res.json().catch(() => ({}));
			if (!res.ok) throw new Error("Unable to load variant details");

			const pricing = data.smProductVariantPricingResponseDTO;
			const inventory = data.smProductVariantInventoryResponseDTO;
			if (!pricing?.id || !inventory?.id) {
				setSnackbar({ open: true, severity: "error", message: "Unable to load variant details." });
				return;
			}

			setVariantIdValue(variantObj.id);
			setVariantPricingId(pricing.id);
			setVariantInventoryId(inventory.id);
			setVariantPreferenceColorId(null);
			setVariantPreferenceColorCodeId(null);
			setVariantPreferenceSizeId(null);

			let color = variantObj.color || "";
			let colorCode = variantObj.colorCode || "";
			let size = variantObj.size || "";
			(data.smProductVariantPreferenceResponseDTO || []).forEach((pref) => {
				switch (pref.type) {
					case "color":
						setVariantPreferenceColorId(pref.id);
						color = pref.value || color;
						break;
					case "colorCode":
						setVariantPreferenceColorCodeId(pref.id);
						colorCode = pref.value || colorCode;
						break;
					case "size":
						setVariantPreferenceSizeId(pref.id);
						size = pref.value || size;
						break;
					default:
						break;
				}
			});

			setEditingVariant(variantObj);
			setVariantForm({
				id: variantObj.id,
				color,
				colorCode,
				size,
				lowStock: inventory.lowStock ?? variantObj.lowStock ?? 0,
				maxStock: inventory.maxStock ?? variantObj.maxStock ?? 0,
				currentStock: inventory.inStock ?? variantObj.currentStock ?? 0,
				pricePerUnit: pricing.pricePerUnit ?? variantObj.pricePerUnit ?? 0,
				units: pricing.units ?? variantObj.units ?? 0,
				quantity: pricing.quantity ?? variantObj.quantity ?? 0,
				totalPrice: pricing.totalPrice ?? variantObj.totalPrice ?? 0,
				barcode: pricing.barcode ?? variantObj.barcode ?? "",
				sku: pricing.sku ?? pricing.SKU ?? variantObj.sku ?? "",
				images: variantObj.images || [],
				imageFiles: variantObj.imageFiles || [],
			});
			setVariantDialogOpen(true);
		} catch (err) {
			console.error(err);
			setSnackbar({ open: true, severity: "error", message: "Unable to load variant details." });
		} finally {
			setLoadBackdrop(false);
		}
	};

	const deleteVariant = (id) =>
		setFormData((prev) => ({
			...prev,
			variants: prev.variants.filter((v) => v.id !== id),
		}));

	const sections = [
		{ id: "product-details", label: "Product Details", icon: InventoryIcon, color: "#2563EB" },
		{ id: "pricing", label: "Pricing", icon: AttachMoneyIcon, color: "#16A34A" },
		{ id: "images", label: "Images", icon: CloudUploadIcon, color: "#9333EA" },
		{ id: "inventory", label: "Inventory", icon: Inventory2OutlinedIcon, color: "#F97316" },
		{ id: "variations", label: "Variations", icon: LayersIcon, color: "#4F46E5" },
		{ id: "shipping", label: "Shipping", icon: LocalShippingIcon, color: "#0D9488" },
		{ id: "additional", label: "Additional", icon: SettingsIcon, color: "#6B7280" },
	];

	const handleSectionChange = (id) => {
		setActiveSection((prev) => (prev === id ? "" : id));
	};
	const completedSections = getCompletedSections();
	const canSave = completedSections.length >= 5; // min required
	const nextIncomplete = sections.find((s) => !completedSections.includes(s.id));


	const ColorPicker = ({ value, onChange }) => (
		<Box sx={{ display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 1 }}>
			{colorPalette.map((c) => (
				<Box
					key={c}
					component="button"
					type="button"
					onClick={() => onChange(c)}
					sx={{
						width: 24,
						height: 24,
						borderRadius: "6px",
						border: "2px solid",
						borderColor: value === c ? "#000" : "#E5E7EB",
						transform: value === c ? "scale(1.15)" : "scale(1)",
						transition: "all 0.1s ease-in-out",
						backgroundColor: c,
						cursor: "pointer",
						p: 0,
					}}
				/>
			))}
			<Box sx={{ gridColumn: "span 8", display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
				<Typography variant="caption">Custom:</Typography>
				<input
					type="color"
					value={value || "#000000"}
					onChange={(e) => onChange(e.target.value)}
					style={{ width: 36, height: 24, border: "1px solid #ccc", borderRadius: 4, padding: 0 }}
				/>
				<Typography variant="caption" color="text.secondary">{value}</Typography>
			</Box>
		</Box>
	);

	const SizeSelector = ({ value, onChange }) => {
		const [selectedCategory, setSelectedCategory] = useState("Clothing");
		const [selectedSubCategory, setSelectedSubCategory] = useState("Unisex");

		const sizesForSelected =
			sizeCategories[selectedCategory]?.[selectedSubCategory] || [];

		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
					<Select
						size="small"
						value={selectedCategory}
						onChange={(e) => {
							setSelectedCategory(e.target.value);
							setSelectedSubCategory(Object.keys(sizeCategories[e.target.value])[0]);
						}}
					>
						{Object.keys(sizeCategories).map((cat) => (
							<MenuItem key={cat} value={cat}>{cat}</MenuItem>
						))}
					</Select>
					<Select
						size="small"
						value={selectedSubCategory}
						onChange={(e) => setSelectedSubCategory(e.target.value)}
					>
						{Object.keys(sizeCategories[selectedCategory]).map((sub) => (
							<MenuItem key={sub} value={sub}>{sub}</MenuItem>
						))}
					</Select>
				</Box>

				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(4,1fr)", sm: "repeat(6,1fr)" }, gap: 1 }}>
					{sizesForSelected.map((sz) => (
						<Button
							key={sz}
							variant={value === sz ? "contained" : "outlined"}
							size="small"
							onClick={() => onChange(sz)}
							sx={{
								textTransform: "none",
								minWidth: 0,
								px: 1,
								fontSize: 12,
							}}
						>
							{sz}
						</Button>
					))}
				</Box>

				{value && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Chip label={value} size="small" />
						<IconButton size="small" onClick={() => onChange("")}>
							<DeleteIcon fontSize="inherit" />
						</IconButton>
					</Box>
				)}
			</Box>
		);
	};

	// Sidebar progress + section nav (desktop)
	const SidebarContent = () => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
			{/* progress */}
			<Box>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
					<Typography variant="subtitle1" fontWeight={600}>Progress</Typography>
					<Typography variant="caption" color="text.secondary">
						{Math.round(completionPct)}%
					</Typography>
				</Box>
				<LinearProgress
					variant="determinate"
					value={completionPct}
					sx={{ height: 6, borderRadius: 1 }}
				/>
				<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
					{completedSections.length} of {sections.length} sections completed
				</Typography>
			</Box>

			{/* section nav */}
			<Box>
				<Typography variant="subtitle1" fontWeight={600} gutterBottom>Sections</Typography>
				<Typography variant="caption" color="text.secondary">
					Complete all required sections to save your product.
				</Typography>
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
				{sections.map((s) => {
					const Icon = s.icon;
					const active = activeSection === s.id;
					const done = completedSections.includes(s.id);
					return (
						<Button
							key={s.id}
							variant={active ? "contained" : "text"}
							color={active ? "primary" : "inherit"}
							onClick={() => handleSectionChange(s.id)}
							sx={{
								justifyContent: "flex-start",
								borderRadius: 2,
								textTransform: "none",
								px: 1.5,
								py: 1.25,
								gap: 1.5,
								...(active
									? { background: "linear-gradient(90deg,#2563EB,#9333EA)", color: "#fff" }
									: { color: "text.primary", "&:hover": { bgcolor: "#F1F5F9" } }),
							}}
							startIcon={<Icon sx={{ fontSize: 20, color: active ? "#fff" : s.color }} />}
							endIcon={
								done ? (
									<CheckIcon sx={{ fontSize: 18, color: active ? "#fff" : "#16A34A" }} />
								) : null
							}
						>
							{s.label}
						</Button>
					);
				})}
			</Box>

			{!canSave && (
				<Alert severity="info" sx={{ mt: 2 }}>
					Complete all required sections to enable saving.
				</Alert>
			)}
		</Box>
	);

	const handleSaveProduct = async (e) => {
		if (e) e.preventDefault();

		const required = [
			{ v: formData.productName, label: "Product Name" },
			{ v: formData.displayName, label: "Product Display Name" },
			{ v: formData.barcode, label: "Barcode" },
			{ v: formData.pricePerUnit, label: "Price Per Unit" },
			{ v: formData.units, label: "Units" },
			{ v: formData.quantity, label: "Quantity" },
			{ v: formData.totalPrice, label: "Total Price" },
			{ v: formData.maxStock, label: "Max Stock" },
			{ v: formData.currentStock, label: "In Stock" },
			{ v: formData.lowStock, label: "Low Stock" },
			{ v: formData.fulfillmentMethod, label: "Fulfillment" },
			{ v: formData.productStatus, label: "Product Status" },
			{
				v: formData.deliveryOptions.length,
				label: "Delivery Option",
				isList: true,
			},
			{ v: formData.shippingWeight, label: "Shipping Weight" },
		];

		const missing = required
			.filter((f) => (f.isList ? f.v === 0 : !f.v))
			.map((f) => f.label);

		if (missing.length) {
			setSnackbar({
				open: true,
				severity: "warning",
				message: `Missing: ${missing.join(", ")}`,
			});
			return;
		}

		const productDetails = {
			id: product.id,
			vendorId: product.vendorId,
			department: product.department,
			category: product.category,
			subCategory: product.subCategory,
			name: formData.productName,
			displayName: formData.displayName,
			shortDescription: formData.shortDescription,
			description: formData.description,
			creationDateTime: Date.now(),
			createdBy: getUserName(),
			updatedDateTime: Date.now(),
			updatedBy: getUserName(),
		};

		const pricingDetails = {
			productId: product.id,
			vendorId: product.vendorId,
			smProductPricing: {
				id: pricing.id,
				barcode: formData.barcode,
				totalPrice: formData.totalPrice,
				pricePerUnit: formData.pricePerUnit,
				units: formData.units,
				quantity: formData.quantity,
				contactSupplierFlag: formData.contactSupplier,
				creationDateTime: Date.now(),
				createdBy: getUserName(),
				updatedDateTime: Date.now(),
				updatedBy: getUserName(),
				sku: formData.sku,
			},
		};

		const imageFilesPayload = {};
		["primary", "imgSec1", "imgSec2", "imgSec3", "imgSec4"].forEach((k, i) => {
			imageFilesPayload[k] = formData.imageFileObjects[i] || null;
		});

		const inventoryDetails = {
			productId: product.id,
			vendorId: product.vendorId,
			smProductInventory: {
				id: inventory.id,
				barcode: formData.barcode,
				inStock: formData.currentStock,
				lowStock: formData.lowStock,
				lowStockTrack: formData.lowStockTracking,
				maxStock: formData.maxStock,
				creationDateTime: Date.now(),
				createdBy: getUserName(),
				updatedDateTime: Date.now(),
				updatedBy: getUserName(),
				sku: formData.sku,
			},
		};

		const imageSections = ["primary", "imgSec1", "imgSec2", "imgSec3", "imgSec4"];
		const imageDetails = {};
		formData.images.forEach((url, idx) => {
			const key = imageSections[idx] || `imgSec${idx}`;
			imageDetails[key] = url;
		});

		const colorName =
			formData.productColorName?.startsWith("#")
				? (HEX_TO_NAME[formData.productColorValue?.toLowerCase()] || "Custom")
				: (formData.productColorName || "");

		const productPreference = {
			productId: product.id,
			smProductPreference: [
				{ type: "color", value: colorName ?? "" },
				{ type: "colorCode", value: formData.productColorValue ?? "" },
				{ type: "size", value: formData.productSize ?? "" },
			],
		};

		const fulfillmentDetails = {
			productId: product.id,
			vendorId: product.vendorId,
			smProductFulfilment: {
				id: fulfilment.id,
				deliveryOption: formData.deliveryOptions.map(toApiDeliveryCode).join(","),
				fulfillment: formData.fulfillmentMethod,
				productStatus: formData.productStatus,
				creationDateTime: Date.now(),
				createdBy: getUserName(),
				updatedDateTime: Date.now(),
				updatedBy: getUserName(),
			},
		};

		const additionalInformation = {
			productId: product.id,
			vendorId: product.vendorId,
			smProductAdditionalInfo: {
				id: additionInfo.id,
				shippingWeight: formData.shippingWeight,
				specialIndicators: formData.additionalInfo.join(","),
				creationDateTime: Date.now(),
				createdBy: getUserName(),
				updatedDateTime: Date.now(),
				updatedBy: getUserName(),
			},
		};

		const supplierInfo = {
			productId: product.id,
			vendorId: product.vendorId,
			smSupplierInformation: { ...formData.supplierInfo },
		};

		const payload = {
			subCategory: product.subCategory,
			productID: String(product.id),
			productDetails,
			pricingDetails,
			inventoryDetails,
			productPreference,
			imageDetails,
			imageFiles: imageFilesPayload,
			fulfillmentDetails,
			additionalInformation,
			supplierInfo,
		};
		setLoadBackdrop(true);
		try {
			await dispatch(saveProductThunk(payload)).unwrap();
			setSnackbar({ open: true, severity: "success", message: "Product saved." });
		} catch (err) {
			console.error(err);
			const failed = Array.isArray(err?.failed) ? err.failed : [];
			const detail = failed
				.map((f) => `${SAVE_STEP_LABELS[f.step] || f.step} (${f.message})`)
				.join(", ");
			setSnackbar({
				open: true,
				severity: "error",
				message: detail ? `Not saved: ${detail}. Fix and save again.` : "Could not save product.",
			});
		} finally {
			setLoadBackdrop(false);
		}
	};

	const handleAiEnhanceDescription = () => {
		if (!formData.productName || !formData.description) {
			setSnackbar({
				open: true,
				severity: "info",
				message: "Product name & description required before enhancing.",
			});
			return;
		}

		setLoadBackdrop(true);
		fetch(
			`${config.API_MWSSTOREMANAGERAI_BASE_URL}/api/v1/enhance-product-info`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Session-Token": sessionToken()
				},
				body: JSON.stringify({
					productName: formData.productName,
					productCategory: product.subCategory || "",
					productDescription: formData.description,
				}),
			}
		)
			.then((r) => {
				const aiMsg = aiErrorMessage(r.status);
				if (aiMsg) {
					const aiErr = new Error(aiMsg);
					aiErr.userMessage = aiMsg;
					throw aiErr;
				}
				return r.json();
			})
			.then((data) => {
				if (data.error) throw new Error(data.error);
				setFormData((prev) => ({
					...prev,
					displayName: data.name || prev.displayName,
					description: data.description || prev.description,
				}));
				setSnackbar({
					open: true,
					severity: "info",
					message: "Description enhanced by AI.",
				});
			})
			.catch((err) =>
				setSnackbar({
					open: true,
					severity: "error",
					message: err.userMessage || "AI enhancement failed – please try again.",
				})
			)
			.finally(() => setLoadBackdrop(false));
	};

	// ───────────────── number field guards & helpers ─────────────────
	const blockInvalidKeys = (e, { allowDot = false } = {}) => {
		const bad = ["e", "E", "+", "-"];
		if (!allowDot) bad.push(".");
		if (bad.includes(e.key) || e.key === "ArrowUp" || e.key === "ArrowDown") {
			e.preventDefault();
		}
	};

	// apply to <input>, not the TextField root
	const commonWheelBlock = (e) => e.currentTarget.blur();

	// keep empty string while typing; clamp to non-negative when present
	const clampNonNeg = (raw, { float = false } = {}) => {
		if (raw === "" || raw === null || raw === undefined) return "";
		const n = float ? parseFloat(raw) : parseInt(raw, 10);
		if (Number.isNaN(n)) return "";
		return Math.max(0, n);
	};

	// pretty display: add leading 0 for decimals like ".25" → "0.25"
	const withLeadingZero = (v) => {
		if (v === "" || v === null || v === undefined) return "";
		const s = String(v);
		return s.startsWith(".") ? `0${s}` : s;
	};

	// helpers to update main form
	const onIntChange = (key) => (e) =>
		setFormData((p) => ({ ...p, [key]: clampNonNeg(e.target.value) }));

	const onDecChange = (key) => (e) =>
		setFormData((p) => ({ ...p, [key]: clampNonNeg(e.target.value, { float: true }) }));

	// helpers to update variant form
	const onIntChangeV = (key) => (e) =>
		setVariantForm((p) => ({ ...p, [key]: clampNonNeg(e.target.value) }));

	const onDecChangeV = (key) => (e) =>
		setVariantForm((p) => ({ ...p, [key]: clampNonNeg(e.target.value, { float: true }) }));


	return (
		<Box
			sx={{
				overflowX: "hidden", // Fix horizontal scroll
				minHeight: "100vh",
				bgcolor: "linear-gradient(to bottom right, #f1f5f9, #f8fafc)",
				// hide number spinners across browsers
				"& input[type=number]": { MozAppearance: "textfield" },
				"& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
				"& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
			}}
		>
			<ThemeProvider theme={theme}>
				<GlobalStyles
					styles={{
						/* Hide number spinners in WebKit and Firefox */
						'input[type="number"]': { MozAppearance: "textfield" },
						'input[type="number"]::-webkit-outer-spin-button': { WebkitAppearance: "none", margin: 0 },
						'input[type="number"]::-webkit-inner-spin-button': { WebkitAppearance: "none", margin: 0 },
					}}
				/>
				{/* Header */}
				<Box
					sx={{
						position: "sticky",
						top: 0,
						zIndex: 1100,
						bgcolor: "rgba(255,255,255,0.8)",
						backdropFilter: "blur(8px)",
						borderBottom: "1px solid #E5E7EB",
					}}
				>
					<Container maxWidth="lg" sx={{ py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
							<Button
								onClick={() => {
									saveSelection({
										departmentId: selectedDepartmentId,
										categoryId: selectedCategoryId,
										subCategoryId: selectedSubCategoryId,
									});
									navigate(-1);
								}}
								startIcon={<ArrowBackIosNewIcon />}
								variant="outlined"
								size="small" color="secondary"
								sx={{
									borderRadius: 999,
									textTransform: "none",
									px: 1.25, py: 0.75, fontWeight: 600, mr: isMdUp ? 0 : 1,
									borderColor: "divider",
									bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
									"&:hover": { bgcolor: (t) => t.palette.action.hover },
								}}
							>
								Back
							</Button>
							<Box>
								<Typography variant="h6" sx={{ lineHeight: 1.2 }}>
									Edit Product
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
									{product?.department || ""} • {product?.category || ""} • {product?.subCategory || ""} &nbsp;•&nbsp;
									Product ID: {product?.id ?? ""}
								</Typography>
							</Box>
						</Box>

						<Button
							variant="contained"
							onClick={() => handleSaveProduct()}
							disabled={!canSave}
							startIcon={<SaveIcon />}
							color="primary"
							sx={{
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							{isMdUp ? "Save Changes" : "Save"}
						</Button>
					</Container>
				</Box>

				<Box sx={{ px: 1 }}>
					<Container
						maxWidth="lg"
						sx={{
							display: "grid",
							overflowX: "hidden",
							py: { xs: 0, sm: 4 }, px: 0,
							gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
							gap: { xs: 3, md: 4 },
						}}
					>
						{/* Sidebar (md+) */}
						<Box sx={{ display: { xs: "none", md: "block" }, position: "relative" }}>
							<Card sx={{ position: "sticky", top: 96, p: 2 }}>{/* 96 ~ header height + margin */}
								<SidebarContent />
							</Card>
						</Box>

						{/* Main accordion content */}
						<Box>
							{/* Mobile progress card */}
							<Box sx={{ display: { xs: "block", md: "none" }, mb: 3 }}>
								<Card>
									<CardContent sx={{ p: 2 }}>
										<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
											<Typography variant="subtitle2" fontWeight={600}>Progress</Typography>
											<Typography variant="caption" color="text.secondary">{Math.round(completionPct)}%</Typography>
										</Box>
										<LinearProgress variant="determinate" value={completionPct} sx={{ height: 6, borderRadius: 1 }} />
										<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
											{completedSections.length} of {sections.length} sections completed
										</Typography>
									</CardContent>
								</Card>
							</Box>
							<Accordion
								expanded={activeSection === "product-details"}
								onChange={() => handleSectionChange("product-details")}
								disableGutters
								sx={{
									mb: 2,
									border: "1px solid #E5E7EB",
									borderRadius: 2,
									"&:before": { display: "none" },
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{
										px: { xs: 2, sm: 3 },
										py: 1.5,
										"& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 },
									}}
								>
									<Box
										sx={{
											width: 32,
											height: 32,
											borderRadius: 1,
											bgcolor: "rgba(37,99,235,0.12)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<InventoryIcon sx={{ fontSize: 18, color: "#2563EB" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Product Details</Typography>
										<Typography variant="caption" color="text.secondary">
											Basic information about your product
										</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									<Grid container spacing={2} sx={{ mt: 0.5 }}>
										{/* Product Name */}
										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												required
												label="Product Name"
												value={formData.productName}
												error={!formData.productName}
												onChange={(e) =>
													setFormData((p) => ({ ...p, productName: e.target.value }))
												}
												helperText={`${formData.productName.length}/200 characters`}
											/>
										</Grid>
										{/* Display Name */}
										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												required
												label="Display Name"
												value={formData.displayName}
												error={!formData.displayName}
												onChange={(e) =>
													setFormData((p) => ({ ...p, displayName: e.target.value }))
												}
												helperText={`${formData.displayName.length}/200 characters`}
											/>
										</Grid>
										{/* Short Description */}
										<Grid item xs={12}>
											<TextField
												fullWidth
												multiline
												minRows={2}
												label="Short Description"
												value={formData.shortDescription}
												onChange={(e) =>
													setFormData((p) => ({ ...p, shortDescription: e.target.value }))
												}
												helperText={`${formData.shortDescription.length}/250 characters`}
											/>
										</Grid>
										{/* Description */}
										<Grid item xs={12}>
											<Box
												sx={{
													display: "flex",
													alignItems: { xs: "flex-start", sm: "center" },
													justifyContent: "space-between",
													gap: 1,
													mb: 1,
												}}
											>
												<Typography variant="subtitle2">Product Description</Typography>
												<Button
													variant="outlined"
													size="small" color="secondary"
													startIcon={<AutoFixHighIcon />}
													onClick={() => handleAiEnhanceDescription()}
													sx={{ textTransform: "none" }}
												>
													AI Enhance
												</Button>
											</Box>
											<TextField
												fullWidth
												multiline
												minRows={4}
												placeholder="Detailed description of your product"
												value={formData.description}
												onChange={(e) =>
													setFormData((p) => ({ ...p, description: e.target.value }))
												}
												helperText={`${formData.description.length}/350 characters`}
											/>
										</Grid>
									</Grid>
								</AccordionDetails>
							</Accordion>

							{/* ------------------------------------------------------ *
           * PRICING DETAILS ACCORDION
           * ------------------------------------------------------ */}
							<Accordion
								expanded={activeSection === "pricing"}
								onChange={() => handleSectionChange("pricing")}
								disableGutters
								sx={{
									mb: 2,
									border: "1px solid #E5E7EB",
									borderRadius: 2,
									"&:before": { display: "none" },
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{
										px: { xs: 2, sm: 3 },
										py: 1.5,
										"& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 },
									}}
								>
									<Box
										sx={{
											width: 32,
											height: 32,
											borderRadius: 1,
											bgcolor: "rgba(22,163,74,0.12)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<AttachMoneyIcon sx={{ fontSize: 18, color: "#16A34A" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Pricing Details</Typography>
										<Typography variant="caption" color="text.secondary">
											Set your product pricing
										</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									{/* Contact Supplier Flag */}
									<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
										<Checkbox
											id="contact-supplier"
											checked={formData.contactSupplier}
											onChange={(e) =>
												setFormData((p) => ({ ...p, contactSupplier: e.target.checked }))
											}
										/>
										<Typography
											component="label"
											htmlFor="contact-supplier"
											variant="body2"
											sx={{ cursor: "pointer" }}
										>
											Contact Supplier for Pricing
										</Typography>
									</Box>

									<Grid container spacing={2}>
										{/* Barcode */}
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												required
												label="Barcode"
												value={formData.barcode}
												error={!formData.barcode}
												onChange={(e) =>
													setFormData((p) => ({ ...p, barcode: e.target.value }))
												}
											/>
										</Grid>
										{/* SKU */}
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												label="SKU"
												value={formData.sku}
												onChange={(e) =>
													setFormData((p) => ({ ...p, sku: e.target.value }))
												}
											/>
										</Grid>
										{/* Price per Unit */}
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												required
												type="number"
												label="Price per Unit"
												InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
												inputProps={{
													min: 0,
													step: "any",
													inputMode: "decimal",
													onWheel: commonWheelBlock,
													onKeyDown: (e) => blockInvalidKeys(e, { allowDot: true }),
												}}
												value={withLeadingZero(formData.pricePerUnit)}
												error={!formData.pricePerUnit}
												onChange={onDecChange("pricePerUnit")}
											/>
										</Grid>
										{/* Units */}
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												required
												type="number"
												label="Units"
												inputProps={{
													min: 0,
													step: 1,
													inputMode: "numeric",
													onWheel: commonWheelBlock,
													onKeyDown: blockInvalidKeys,
												}}
												value={withLeadingZero(formData.units)}
												error={!formData.units}
												onChange={onIntChange("units")}
											/>
										</Grid>
										{/* Quantity */}
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												required
												type="number"
												label="Quantity"
												inputProps={{
													min: 0,
													step: 1,
													inputMode: "numeric",
													onWheel: commonWheelBlock,
													onKeyDown: blockInvalidKeys,
												}}
												value={withLeadingZero(formData.quantity)}
												error={!formData.quantity}
												onChange={onIntChange("quantity")}
											/>
										</Grid>
										{/* Total Price */}
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												required
												type="number"
												label="Total Price"
												InputProps={{
													startAdornment: <InputAdornment position="start">₹</InputAdornment>,
													readOnly: !formData.contactSupplier,
												}}
												inputProps={{
													min: 0,
													step: "any",
													inputMode: "decimal",
													onWheel: commonWheelBlock,
													onKeyDown: (e) => blockInvalidKeys(e, { allowDot: true }),
												}}
												value={withLeadingZero(formData.totalPrice)}
												onChange={onDecChange("totalPrice")}
												disabled={!formData.contactSupplier}
											/>
										</Grid>
									</Grid>
								</AccordionDetails>
							</Accordion>

							{/* ==== CONTINUES IN PART 4 ==== */}
							{/* ------------------------------------------------------ *
           * PRODUCT IMAGES ACCORDION
           * ------------------------------------------------------ */}
							<Accordion
								expanded={activeSection === "images"}
								onChange={() => handleSectionChange("images")}
								disableGutters
								sx={{ mb: 2, border: "1px solid #E5E7EB", borderRadius: 2, "&:before": { display: "none" } }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: { xs: 2, sm: 3 }, py: 1.5, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 } }}
								>
									<Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "rgba(147,51,234,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
										<CloudUploadIcon sx={{ fontSize: 18, color: "#9333EA" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Product Images</Typography>
										<Typography variant="caption" color="text.secondary">Upload high‑quality images</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									<Typography
										variant="caption"
										color="secondary"
										sx={{ mb: 2, display: "inline-block", cursor: "pointer", textDecoration: "underline" }}
										onClick={() => setGuidelineOpen(true)}
									>
										View Image Guidelines
									</Typography>

									<Grid container spacing={2}>
										{formData.images.map((img, idx) => (
											<Grid item xs={6} sm={4} md={2.4} key={idx}>
												<Box sx={{ position: "relative" }}>
													<Box
														component="img"
														src={img}
														sx={{
															width: "100%",
															height: 120,
															objectFit: "cover",
															borderRadius: 2,
															border: "2px dashed #E5E7EB",
														}}
													/>
													<IconButton
														size="small"
														onClick={() => removeImage(idx)}
														sx={{
															position: "absolute",
															top: 4,
															right: 4,
															bgcolor: "rgba(0,0,0,0.4)",
															color: "#fff",
															"&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
														}}
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
													{idx === 0 && (
														<Chip
															label="Primary"
															size="small"
															sx={{ position: "absolute", bottom: 4, left: 4, bgcolor: "#2563EB", color: "#fff" }}
														/>
													)}
												</Box>
											</Grid>
										))}

										{formData.images.length < 5 && (
											<Grid item xs={6} sm={4} md={2.4}>
												<Button
													fullWidth
													component="label"
													variant="outlined"
													sx={{
														height: 120,
														borderStyle: "dashed",
														borderColor: formData.images.length === 0 ? "#EF4444" : "#A1A1AA",
														color: "#6B7280",
														"&:hover": { borderColor: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" },
														flexDirection: "column",
														gap: 0.5,
														textTransform: "none",
													}}
												>
													<CloudUploadIcon sx={{ fontSize: 24 }} />
													<Typography variant="caption">Upload</Typography>
													<input hidden multiple accept="image/*" type="file" onChange={(e) => handleImageUpload(e)} />
												</Button>
											</Grid>
										)}
									</Grid>

									{formData.images.length === 0 && (
										<Typography variant="caption" color="error.main" sx={{ mt: 1, display: "block" }}>
											At least one image is required
										</Typography>
									)}
								</AccordionDetails>
							</Accordion>

							<Accordion
								expanded={activeSection === "inventory"}
								onChange={() => handleSectionChange("inventory")}
								disableGutters
								sx={{ mb: 2, border: "1px solid #E5E7EB", borderRadius: 2, "&:before": { display: "none" } }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: { xs: 2, sm: 3 }, py: 1.5, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 } }}
								>
									<Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "rgba(249,115,22,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
										<Inventory2OutlinedIcon sx={{ fontSize: 18, color: "#F97316" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Inventory Management</Typography>
										<Typography variant="caption" color="text.secondary">Track stock levels</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												required
												type="number"
												label="Maximum Stock"
												inputProps={{
													min: 0,
													step: 1,
													inputMode: "numeric",
													onWheel: commonWheelBlock,
													onKeyDown: blockInvalidKeys,
												}}
												value={withLeadingZero(formData.maxStock)}
												error={!formData.maxStock}
												onChange={onIntChange("maxStock")}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												required
												type="number"
												label="Current Stock"
												inputProps={{
													min: 0,
													step: 1,
													inputMode: "numeric",
													onWheel: commonWheelBlock,
													onKeyDown: blockInvalidKeys,
												}}
												value={withLeadingZero(formData.currentStock)}
												error={formData.currentStock < 0}
												onChange={onIntChange("currentStock")}
											/>

										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												required
												type="number"
												label="Low Stock Threshold"
												inputProps={{
													min: 0,
													step: 1,
													inputMode: "numeric",
													onWheel: commonWheelBlock,
													onKeyDown: blockInvalidKeys,
												}}
												value={withLeadingZero(formData.lowStock)}
												error={formData.lowStock < 0}
												onChange={onIntChange("lowStock")}
											/>
										</Grid>
									</Grid>

									<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
										<Checkbox
											id="low-stock-track"
											checked={formData.lowStockTracking}
											onChange={(e) => setFormData((p) => ({ ...p, lowStockTracking: e.target.checked }))}
										/>
										<Typography
											component="label"
											htmlFor="low-stock-track"
											variant="body2"
											sx={{ cursor: "pointer" }}
										>
											Enable low stock email notifications
										</Typography>
									</Box>
								</AccordionDetails>
							</Accordion>

							<Accordion
								expanded={activeSection === "variations"}
								onChange={() => handleSectionChange("variations")}
								disableGutters
								sx={{ mb: 2, border: "1px solid #E5E7EB", borderRadius: 2, "&:before": { display: "none" } }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: { xs: 2, sm: 3 }, py: 1.5, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 } }}
								>
									<Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "rgba(79,70,229,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
										<LayersIcon sx={{ fontSize: 18, color: "#4F46E5" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Product Variations</Typography>
										<Typography variant="caption" color="text.secondary">
											Add colors, sizes & other variants
										</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									<Grid container spacing={2} sx={{ mb: 2 }}>
										{/* Colour swatches */}
										<Grid item xs={12} md={6}>
											<FormLabel>Available Colour</FormLabel>
											<Grid container spacing={1} sx={{ mt: 1, maxWidth: 280 }}>
												{colorPalette.map((hex) => (
													<Grid item xs={2} key={hex}>
														<IconButton
															size="small"
															onClick={() => {
																setSelectedColor(hex);
																setFormData((p) => ({
																	...p,
																	productColorValue: hex,
																	productColorName: hex,     // or map‑to‑name if you keep a name map
																}));
															}}
															sx={{
																width: 32,
																height: 32,
																borderRadius: 1,
																bgcolor: hex,
																border:
																	selectedColor === hex
																		? "2px solid #2563eb"
																		: "1px solid #e5e7eb",
																transform: selectedColor === hex ? "scale(1.15)" : "none",
																transition: "all .15s",
															}}
														/>
													</Grid>
												))}

												{/* custom colour picker */}
												<Grid item xs={4}>
													<input
														type="color"
														value={selectedColor || "#000000"}
														onChange={(e) => {
															setSelectedColor(e.target.value);
															setFormData((p) => ({
																...p,
																productColorValue: e.target.value,
																productColorName: e.target.value,
															}));
														}}
														style={{ width: "100%", height: 32, border: "none", padding: 0 }}
													/>
												</Grid>
											</Grid>

											{formData.productColorName && (
												<Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
													Selected: {formData.productColorName}
												</Typography>
											)}
										</Grid>

										{/* Size selector */}
										<Grid item xs={12} md={6}>
											<FormLabel>Available Size</FormLabel>
											<Autocomplete
												freeSolo
												options={sizeOptions}
												groupBy={(o) => o.category}
												getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
												sx={{ mt: 1, maxWidth: 300 }}
												value={
													sizeOptions.find((o) => o.label === formData.productSize) ||
													formData.productSize
												}
												onChange={(_, val) => {
													if (typeof val === "string") {
														setFormData((p) => ({ ...p, productSize: val }));
													} else if (val && val.inputValue) {
														setFormData((p) => ({ ...p, productSize: val.inputValue }));
													} else {
														setFormData((p) => ({ ...p, productSize: val ? val.label : "" }));
													}
												}}
												renderInput={(params) => (
													<TextField size="small" {...params} label="Choose Size" />
												)}
												filterOptions={(opts, params) => {
													const filtered = filterOptions(opts, params);
													const exists = opts.some((o) => params.inputValue === o.label);
													if (params.inputValue !== "" && !exists) {
														filtered.push({
															label: `${params.inputValue}`,
															value: params.inputValue,
															category: "Custom",
															inputValue: params.inputValue,
														});
													}
													return filtered;
												}}
											/>
										</Grid>
									</Grid>
									<Divider sx={{ my: 3 }} />

									{/* --- Variants list & Add button --- */}
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
										<Typography variant="subtitle2">Product Variants</Typography>
										<Button
											variant="outlined"
											size="small" color="secondary"
											startIcon={<AddIcon />}
											onClick={() => {
												resetVariantForm();
												setVariantDialogOpen(true);
											}}
											sx={{ textTransform: "none" }}
										>
											Add Variant
										</Button>
									</Box>

									{formData.variants.length === 0 ? (
										<Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
											<LayersIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1 }} />
											<Typography variant="body2">No variants added yet.</Typography>
											<Typography variant="caption">Add variants to offer multiple options</Typography>
										</Box>
									) : (
										<Grid container spacing={2}>
											{formData.variants.map((v) => (
												<Grid item xs={12} sm={6} md={4} key={v.id}>
													<Card variant="outlined" sx={{ p: 2 }}>
														<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
															<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																{v.colorCode && (
																	<Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: v.colorCode }} />
																)}
																<Chip label={
																	v.variantCode !== "" || null ? v.variantCode : "Untitled Variant"
																} size="small" />
															</Box>
															<Box>
																<IconButton size="small" onClick={() => editVariant(v)}>
																	<EditIcon fontSize="inherit" />
																</IconButton>
																<IconButton size="small" onClick={() => deleteVariant(v.id)}>
																	<DeleteIcon fontSize="inherit" />
																</IconButton>
															</Box>
														</Box>
													</Card>
												</Grid>
											))}
										</Grid>
									)}
								</AccordionDetails>
							</Accordion>

							<Accordion
								expanded={activeSection === "shipping"}
								onChange={() => handleSectionChange("shipping")}
								disableGutters
								sx={{ mb: 2, border: "1px solid #E5E7EB", borderRadius: 2, "&:before": { display: "none" } }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: { xs: 2, sm: 3 }, py: 1.5, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 } }}
								>
									<Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "rgba(13,148,136,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
										<LocalShippingIcon sx={{ fontSize: 18, color: "#0D9488" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Shipping & Fulfillment</Typography>
										<Typography variant="caption" color="text.secondary">Configure delivery options</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									<Grid container spacing={2}>
										{/* Fulfillment Method */}
										<Grid item xs={12} sm={6}>
											<TextField
												select
												fullWidth
												required
												label="Fulfillment Method"
												value={formData.fulfillmentMethod}
												error={!formData.fulfillmentMethod}
												onChange={(e) =>
													setFormData((p) => ({ ...p, fulfillmentMethod: e.target.value }))
												}
											>
												<MenuItem value="Standard Shipping">Standard Shipping</MenuItem>
												<MenuItem value="Standard Shipping and Returns">
													Standard Shipping and Returns
												</MenuItem>
											</TextField>
										</Grid>
										{/* Product Status */}
										<Grid item xs={12} sm={6}>
											<TextField
												select
												fullWidth
												required
												label="Product Status"
												value={formData.productStatus}
												error={!formData.productStatus}
												onChange={(e) =>
													setFormData((p) => ({ ...p, productStatus: e.target.value }))
												}
											>
												<MenuItem value="Available">Available</MenuItem>
												<MenuItem value="Private">Private (Hidden)</MenuItem>
												<MenuItem value="Unavailable">Unavailable</MenuItem>
											</TextField>
										</Grid>
									</Grid>

									{/* Delivery Options */}
									<Box sx={{ mt: 3 }}>
										<Grid item xs={12}>
											<FormControl fullWidth>
												<InputLabel>Delivery Options *</InputLabel>
												<Select
													multiple
													value={formData.deliveryOptions}
													onChange={(e) =>
														setFormData((p) => ({ ...p, deliveryOptions: e.target.value }))
													}
													input={<OutlinedInput label="Delivery Options *" />}
													renderValue={(sel) => sel.join(", ")}
													error={formData.deliveryOptions.length === 0}
												>
													{deliveryOptionsList.map((opt) => (
														<MenuItem key={opt} value={opt}>
															<Checkbox checked={formData.deliveryOptions.indexOf(opt) > -1} />
															<ListItemText primary={opt} />
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
									</Box>

									{/* Shipping weight */}
									<TextField
										fullWidth
										type="number"
										label="Shipping Weight (kg)"
										sx={{ mt: 3, maxWidth: 240 }}
										inputProps={{
											min: 0,
											step: "any",
											inputMode: "decimal",
											onWheel: commonWheelBlock,
											onKeyDown: (e) => blockInvalidKeys(e, { allowDot: true }),
										}}
										value={withLeadingZero(formData.shippingWeight)}
										onChange={onDecChange("shippingWeight")}
										onBlur={(e) => {
											// ensure a leading zero is shown after leaving the field
											const v = e.target.value;
											if (v && v.startsWith(".")) {
												setFormData((p) => ({ ...p, shippingWeight: `0${v}` }));
											}
										}}
									/>
								</AccordionDetails>
							</Accordion>

							{/* ------------------------------------------------------ *
           * ADDITIONAL INFORMATION ACCORDION
           * ------------------------------------------------------ */}
							<Accordion
								expanded={activeSection === "additional"}
								onChange={() => handleSectionChange("additional")}
								disableGutters
								sx={{ mb: 2, border: "1px solid #E5E7EB", borderRadius: 2, "&:before": { display: "none" } }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: { xs: 2, sm: 3 }, py: 1.5, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 2, m: 0 } }}
								>
									<Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: "rgba(107,114,128,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
										<SettingsIcon sx={{ fontSize: 18, color: "#6B7280" }} />
									</Box>
									<Box sx={{ textAlign: "left" }}>
										<Typography variant="subtitle2" fontWeight={600}>Additional Information</Typography>
										<Typography variant="caption" color="text.secondary">
											Extra details & supplier info
										</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
									{/* Special indicators */}
									<Autocomplete
										multiple
										freeSolo
										options={additionalInfoOptions}
										value={formData.additionalInfo}
										onChange={(_, val) => {
											const finalValues = val.map(v => {
												if (typeof v === "string" && v.startsWith('Add "')) {
													return v.replace('Add "', '').replace('"', '');
												}
												return v;
											});
											setFormData((p) => ({ ...p, additionalInfo: finalValues }))
										}}
										filterOptions={(options, params) => {
											const filtered = filterOptions(options, params);
											const { inputValue } = params;
											const isExisting = options.some((option) => inputValue === option);
											if (inputValue !== '' && !isExisting) {
												filtered.push(`Add "${inputValue}"`);
											}
											return filtered;
										}}
										getOptionLabel={(option) => {
											if (typeof option === 'string' && option.startsWith('Add "')) {
												return option.replace('Add "', '').replace('"', '');
											}
											return option;
										}}
										renderTags={(value, getTagProps) =>
											value.map((option, index) => (
												<Chip
													variant="outlined"
													label={option}
													{...getTagProps({ index })}
												/>
											))
										}
										renderInput={(params) => (
											<TextField
												size="small"
												{...params}
												label="Special indicators"
												helperText="Type and press enter to add values"
											/>
										)}
										sx={{ mb: 3 }}
									/>
								</AccordionDetails>
							</Accordion>

							<Dialog
								open={variantDialogOpen}
								onClose={() => setVariantDialogOpen(false)}
								fullWidth
								maxWidth="md"
							>
								<DialogTitle>{editingVariant ? "Edit" : "Add"} Product Variant</DialogTitle>

								<DialogContent
									dividers
									sx={{
										pt: 3,
										"& input[type=number]": { MozAppearance: "textfield" },
										"& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
										"& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
									}}
								>
									{/* --- Color & Size pickers --- */}
									<Grid container spacing={3}>
										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" gutterBottom>Color Selection</Typography>
											<ColorPicker
												value={variantForm.colorCode}
												onChange={(c) =>
													setVariantForm((p) => ({ ...p, colorCode: c, color: c /* store name if needed */ }))
												}
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" gutterBottom>Size Selection</Typography>
											<SizeSelector
												value={variantForm.size}
												onChange={(sz) => setVariantForm((p) => ({ ...p, size: sz }))}
											/>
										</Grid>
									</Grid>

									<Divider sx={{ my: 3 }} />

									{/* --- Stock details --- */}
									<Typography variant="subtitle2" gutterBottom>Stock Details</Typography>
									<Grid container spacing={2} sx={{ mb: 3 }}>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth label="Maximum Stock *"
												{...numberFieldProps}
												value={variantForm.maxStock || ""}
												onChange={onNumChangeV("maxStock")} />
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth label="Current Stock *"
												{...numberFieldProps}
												value={variantForm.currentStock || ""}
												onChange={onNumChangeV("currentStock")} />
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth label="Low Stock *"
												{...numberFieldProps}
												value={variantForm.lowStock || ""}
												onChange={onNumChangeV("lowStock")} />
										</Grid>
									</Grid>

									<Divider sx={{ my: 3 }} />

									{/* --- Price details --- */}
									<Typography variant="subtitle2" gutterBottom>Price Details</Typography>
									<Grid container spacing={2} sx={{ mb: 3 }}>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth label="Price per Unit *"
												{...numberFieldProps}
												InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
												value={variantForm.pricePerUnit || ""}
												onChange={onNumChangeV("pricePerUnit", /*isFloat=*/true)} />
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth label="Units *"
												{...numberFieldProps}
												value={variantForm.units || ""}
												onChange={onNumChangeV("units")} />
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth label="Quantity *"
												{...numberFieldProps}
												value={variantForm.quantity || ""}
												onChange={onNumChangeV("quantity")} />
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField fullWidth disabled label="Total Price *"
												InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
												value={variantForm.totalPrice || ""}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												label="Barcode *"
												value={variantForm.barcode}
												onChange={(e) => setVariantForm((p) => ({ ...p, barcode: e.target.value }))}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												label="SKU *"
												value={variantForm.sku}
												onChange={(e) => setVariantForm((p) => ({ ...p, sku: e.target.value }))}
											/>
										</Grid>
									</Grid>

									<Divider sx={{ my: 3 }} />

									{/* --- Variant images --- */}
									<Typography variant="subtitle2" gutterBottom>Variant Images *</Typography>
									<Grid container spacing={2}>
										{variantForm.images.map((img, idx) => (
											<Grid item xs={6} sm={2.4} key={idx}>
												<Box sx={{ position: "relative" }}>
													<Box
														component="img"
														src={img}
														sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1, border: "1px solid #E5E7EB" }}
													/>
													<IconButton
														size="small"
														onClick={() => removeImage(idx, true)}
														sx={{
															position: "absolute",
															top: 2,
															right: 2,
															bgcolor: "rgba(0,0,0,0.35)",
															color: "#fff",
															"&:hover": { bgcolor: "rgba(0,0,0,0.55)" }
														}}
													>
														<CloseIcon fontSize="small" />
													</IconButton>
													{idx === 0 && (
														<Chip
															label="Primary"
															size="small"
															sx={{ position: "absolute", bottom: 2, left: 2, bgcolor: "#2563EB", color: "#fff" }}
														/>
													)}
												</Box>
											</Grid>
										))}

										{variantForm.images.length < 5 && (
											<Grid item xs={6} sm={2.4}>
												<Button
													fullWidth
													component="label"
													variant="outlined"
													sx={{
														height: 90,
														borderStyle: "dashed",
														borderColor: variantForm.images.length === 0 ? "#EF4444" : "#A1A1AA",
														color: "#6B7280",
														textTransform: "none",
														flexDirection: "column",
														gap: 0.5,
														"&:hover": { borderColor: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" },
													}}
												>
													<CloudUploadIcon sx={{ fontSize: 22 }} />
													<Typography variant="caption">Upload</Typography>
													<input hidden multiple accept="image/*" type="file" onChange={(e) => handleImageUpload(e, true)} />
												</Button>
											</Grid>
										)}
									</Grid>
								</DialogContent>

								<DialogActions>
									<Button onClick={() => setVariantDialogOpen(false)} color="secondary">Cancel</Button>
									<Button variant="contained" onClick={handleVariantSubmit}>
										{editingVariant ? "Update" : "Add"} Variant
									</Button>
								</DialogActions>
							</Dialog>

							<Backdrop
								open={loadBackdrop}
								sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1400 }}
							>
								<CircularProgress color="inherit" />
							</Backdrop>

							<ImageGuidelinesDialog
								open={guidelineOpen}
								onClose={() => setGuidelineOpen(false)}
							/>

							<Snackbar
								open={snackbar.open}
								autoHideDuration={4000}
								onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							>
								<Alert
									onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
									severity={snackbar.severity || "success"}
									sx={{ width: "100%" }}
								>
									{snackbar.message}
								</Alert>
							</Snackbar>
						</Box>
					</Container>
				</Box>
			</ThemeProvider>
		</Box>
	);
}
